import type React from 'react';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { CompanyCard } from '../../components/company-card';
import { CompanyCardContainer } from '../../components/company-card-container';
import { MetaData } from '../../components/meta-data';
import { PageError } from '../../components/page-error';
import { ShowMoreButton } from '../../components/show-more-button';
import { CompanyCardsSkeletonLoggedin } from '../../components/skeleton';
import { useCompanyData } from '../../hooks/use-company-data/use-company-data';
import { useFollowedCompanies } from '../../hooks/use-followed-companies';
import { useTrackPage } from '../../hooks/use-track-page';
import type { Company } from '../../types/companies-data-store';
import {
  pagesTrackingName,
  useNwtTrackingCompanies,
} from '../../utils/tracking';

import * as Styled from './followed-companies.styles';
import { FollowedEmptyState } from './followed-empty-state';

export const FollowedCompanies: React.FC = () => {
  const addCompanies = useCompanyData(({ addCompanies }) => addCompanies);
  const globalCompaniesState = useCompanyData(
    ({ globalCompaniesState }) => globalCompaniesState
  );
  const trackNwtEvent = useNwtTrackingCompanies();

  const { data, loading, error, networkStatus, pagination } =
    useFollowedCompanies();

  const currentCompanies = data?.viewer?.followedCompanies?.edges;

  useEffect(() => {
    if (currentCompanies) {
      const companies = currentCompanies.reduce<Company[]>((_, item) => {
        if (item?.node?.company) return [..._, item.node.company];
        return _;
      }, []);

      addCompanies({ companies });
    }
  }, [currentCompanies]);
  const intl = useIntl();

  useTrackPage({ pageName: pagesTrackingName.followed });

  useEffect(() => {
    trackNwtEvent({
      page: 'companies/following',
      event: 'viewed_screen',
      eventSchema: 'basic',
    });
  }, []);

  const loadingFirstRender = loading && networkStatus !== 3;
  if (loadingFirstRender) return <CompanyCardsSkeletonLoggedin />;
  if (error) return <PageError />;
  if (currentCompanies?.length === 0) return <FollowedEmptyState />;

  const { hasNextPage, endCursor } =
    data?.viewer?.followedCompanies?.pageInfo ?? {};

  const isPaginationDisabled = () => {
    if (loadingFirstRender) return true;

    return false;
  };

  const companies = currentCompanies?.reduce<Company[]>((_, item) => {
    if (item?.node?.company) return [..._, item.node.company];
    return _;
  }, []);

  return (
    <>
      <MetaData
        index={false}
        title={intl.formatMessage({
          id: 'CP_100_MYCP_FOLLOWED_COMPANIES_TITLE_NEW',
        })}
        description={intl.formatMessage({
          id: 'CP_100_MYCP_FOLLOWED_COMPANIES_TITLE_NEW',
        })}
      />
      <Styled.Headline size="medium">
        <FormattedMessage id="CP_100_MYCP_FOLLOWED_COMPANIES_TITLE_NEW" />
      </Styled.Headline>
      <CompanyCardContainer>
        {companies?.map((company, index) => (
          <CompanyCard
            key={company?.id}
            company={company}
            buttons={
              globalCompaniesState.length > 0
                ? [
                    <Styled.FollowButton
                      key={company?.id}
                      disabled={company?.entityPageId ? false : true}
                      entityPageId={company?.entityPageId as string}
                      isFollowing={
                        company?.userContext?.followState?.isFollowing ?? false
                      }
                      companyId={company?.id}
                      followText="CP_200_FOLLOW_LINK_BACK"
                      position={index}
                    />,
                  ]
                : []
            }
            onClick={() =>
              trackNwtEvent({
                eventSchema: 'basic',
                event: 'opened',
                element: 'company_card',
                page: 'companies/following',
                position: index,
                itemUrn: `surn:x-xing:companies:company:${company.id}`,
              })
            }
            onWasSeen={() => {
              trackNwtEvent({
                page: 'companies/following',
                eventSchema: 'basic',
                event: 'entered_viewport',
                element: 'company_card',
                position: index,
                itemUrn: `surn:x-xing:companies:company:${company?.id}`,
              });
            }}
          />
        ))}
      </CompanyCardContainer>
      {hasNextPage && (
        <ShowMoreButton
          disabled={isPaginationDisabled()}
          loading={loading}
          onClick={() => pagination(endCursor as string)}
          intlKey="CP_OTHER_COMPANIES_FOLLOW"
        />
      )}
    </>
  );
};
